// hooks
import { useCallback, useEffect, useState, Fragment } from "react";
import useHttp from "hooks/use-http";

import { useForm, Controller } from "react-hook-form";

// styled component
import { StyledStack, StyledMenuItem, StyledGridIcons } from "./products.style";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

// material icons
import { Delete, Edit, FilterAlt } from "@mui/icons-material";

import CheckIcon from "@mui/icons-material/Check";

import { MdDelete, MdEdit } from "react-icons/md";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";

// helper
import GenerateHeadCell from "helper/generate-head-cell";
import Toast from "helper/toast";

// constants
import { PAGINATION_LIMIT } from "constants";

import { useNavigate } from "react-router-dom";
import {
  getAllProductsDataRequest,
  getProductsCategoryRequest,
} from "api/requests/products";

// products head cells
const headCells = GenerateHeadCell("products_list");

function DisplayProducts() {
  const { request, loading, setLoading } = useHttp(false);

  const {
    register,
    control,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  // states for filtered
  const [searchValue, setSearchValue] = useState("");

  // products data state
  const [productsData, setProductsData] = useState({
    data: [],
    count: 0,
  });

  // state for filter
  const [allCategories, setAllCategories] = useState({ data: [], count: 0 });
  const [categoryList, setCategoryList] = useState({
    data: [],
    count: 0,
  });
  const [subCategoryList, setSubCategoryList] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState();

  const [selectedSubCategory, setSelectedSubCategory] = useState();

  // page state for pagination
  const [page, setPage] = useState(1);

  // get all products data
  const getProductsData = async () => {
    setLoading(true);

    const categoryRes = await request({
      url: getProductsCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
        is_parent: 0, // get all categoies and subcategories.
      },
      isMulty: true,
    });
    if (categoryRes) {
      setAllCategories({
        data: categoryRes.data.data,
        count: categoryRes.data.count,
      });
      let categoriesData = categoryRes.data.data.filter(
        (item) => item.parent_id == null
      );
      setCategoryList({ data: categoriesData, count: categoriesData.length });
    }

    const values = {
      skip: searchValue ? 0 : (page - 1) * PAGINATION_LIMIT,
      limit: searchValue ? 100 : PAGINATION_LIMIT,
      search: searchValue ?? null,
      category: selectedCategory ?? null,
      sub_category: selectedSubCategory ?? null,
    };
    !searchValue.length && delete values.search;
    !selectedCategory && delete values.category;
    !selectedSubCategory && delete values.sub_category;

    const res = await request({
      url: getAllProductsDataRequest,
      values,
      isMulty: true,
    });

    setLoading(false);

    if (res) {
      if (res.data.count < 1) {
        return setProductsData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        product_name: items.title,
        product_category: items?.sub_category?.category?.title
          ? items.sub_category.category.title
          : "ثبت نشده",
        product_subCategoru: items?.sub_category?.title
          ? items.sub_category.title
          : "ثبت نشده",
        edit: (
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/products/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setProductsData({
        data: transformedData,
        count: res.data.count,
      });
    }
    setLoading(false);
  };

  // get products data without filter
  const getProductsDataWithoutFilter = async () => {
    setSelectedCategory("");
    setSearchValue("");
    setSelectedSubCategory("");

    const res = await request({
      url: getAllProductsDataRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
      },
      isMulty: true,
    });

    if (res) {
      if (res.data.count < 1) {
        return setProductsData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        product_name: items.title,
        product_category: items?.sub_category?.category?.title
          ? items.sub_category.category.title
          : "ثبت نشده",
        product_subCategoru: items?.sub_category?.title
          ? items.sub_category.title
          : "ثبت نشده",
        edit: (
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/products/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setProductsData({
        data: transformedData,
        count: res.data.count,
      });
    }
  };

  useEffect(() => {
    setSelectedSubCategory(null);
    let data = allCategories.data.filter(
      (item) => item.parent_id == selectedCategory
    );
    setSubCategoryList({ data: data, count: data.length });
  }, [selectedCategory]);

  useEffect(() => {
    getProductsData();
  }, [page]);
  return (
    <DrawerCM title="محصولات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <StyledStack>
            <Grid container flex alignItems="center">
              <Grid item sm={4} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={3} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_category"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_category">سردسته</InputLabel>
                      <Select
                        id="category"
                        name="category"
                        label="سردسته"
                        labelId="select-category"
                        value={selectedCategory ?? ""}
                        {...register("category")}
                        error={Boolean(errors.category)}
                        helperText={errors.category?.message}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {categoryList?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={3} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_subcategory"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_subcategory">زیردسته</InputLabel>
                      <Select
                        id="subcategory"
                        name="subcategory"
                        label="زیردسته"
                        labelId="select_subcategory"
                        value={selectedSubCategory ?? ""}
                        {...register("subcategory")}
                        error={Boolean(errors.subcategory)}
                        helperText={errors.subcategory?.message}
                        onChange={(e) => setSelectedSubCategory(e.target.value)}
                      >
                        {subCategoryList?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getProductsData}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getProductsDataWithoutFilter}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست محصولات"
                rows={productsData.data}
                headCells={headCells}
                page={page}
                addProducts
                productsCategory
                colspan={productsData.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {productsData.count < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={productsData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}

export default DisplayProducts;
